import { useApiCommunicator } from '@btcdirect/shared/src/composables/api/communicator';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { ApiCommunicationStates } from '@btcdirect/shared/src/composables/api/types';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { WalletAddress, useCurrenciesStore, useWalletAddressesStore } from '@btcdirect/stores';

import { reactive, readonly } from 'vue';

const { warn } = useLogger();

interface UserDcaCode {
    code: string;
    apiClientName?: string;
    cryptoCurrency: string;
    walletAddress: string;
    bankName: string;
    bankIban: string;
    bankBic: string;
    destinationTag?: number;
}

interface DcaRequest {
    cryptoCurrency: string;
    fiatAmount: number;
    fiatCurrency: string;
    recurrence: string;
    communication: ApiCommunicationStates;
    walletAddress: WalletAddress;
}

interface UserDcaCodes {
    codes: UserDcaCode[];
    communication: ApiCommunicationStates;
}

// TODO: Refactor or remove this once the v2 endpoint is created
interface DcaResponseV1 {
    bic: string;
    iban: string;
    name: string;
    description: string;
}

const dcaRequest = reactive<DcaRequest>({
    cryptoCurrency: null,
    fiatAmount: null,
    fiatCurrency: null,
    recurrence: null,
    communication: ApiCommunicationStates.NOT_STARTED,
    walletAddress: null
});

const currentDca = reactive<UserDcaCode>({
    bankBic: null,
    bankIban: null,
    bankName: null,
    code: null,
    cryptoCurrency: null,
    walletAddress: null,
    destinationTag: null,
    apiClientName: null
});

const dcaCodes = reactive<UserDcaCodes>({
    codes: [],
    communication: ApiCommunicationStates.NOT_STARTED
});

export const useDca = () => {
    const { post } = useApiCommunicator(Endpoints.CREATE_DCA_CODE);
    const { get } = useApiCommunicator(Endpoints.GET_DCA_CODES);

    const currenciesStore = useCurrenciesStore();
    const walletAddressesStore = useWalletAddressesStore();

    const requestCode = async (): Promise<void> => {
        dcaRequest.communication = ApiCommunicationStates.COMMUNICATING;

        try {
            const result: DcaResponseV1 = await post({
                currencyPair: currenciesStore.selectedCurrencyPair.currencyPair,
                walletAddress: walletAddressesStore.currentWalletAddress.address,
                destinationTag: walletAddressesStore.currentWalletAddress.destinationTag ?? null
            });

            currentDca.bankBic = result.bic;
            currentDca.bankIban = result.iban;
            currentDca.bankName = result.name;
            currentDca.code = result.description;
            currentDca.cryptoCurrency = currenciesStore.selectedCryptoCurrency.code;
            currentDca.walletAddress = walletAddressesStore.currentWalletAddress.address;

            dcaRequest.communication = ApiCommunicationStates.SUCCESSFUL;
        } catch (exception) {
            warn('Could not save the DCA code');
            dcaRequest.communication = ApiCommunicationStates.FAILED;
            return Promise.reject(exception);
        }
    };

    const requestExistingCodes = async (): Promise<void> => {
        dcaCodes.communication = ApiCommunicationStates.COMMUNICATING;

        try {
            const codes: UserDcaCode[] = await get();

            dcaCodes.codes = codes;
            dcaCodes.communication = ApiCommunicationStates.SUCCESSFUL;
        } catch (exception) {
            warn('Could not retrieve the DCA codes');
            dcaCodes.communication = ApiCommunicationStates.FAILED;
        }
    };

    return {
        dcaRequest,
        currentDca,
        dcaCodes: readonly(dcaCodes),
        requestCode,
        requestExistingCodes
    };
};
